import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { USE_MOCKS } from '@/lib/api';
import { AuthContext } from './context';
import {
  MfaNotSupportedError,
  NewPasswordRequiredError,
  changePassword as cognitoChangePassword,
  completeNewPassword as cognitoCompleteNewPassword,
  confirmPasswordReset as cognitoConfirmPasswordReset,
  decodeIdToken,
  getValidIdToken,
  requestPasswordReset as cognitoRequestPasswordReset,
  signIn as cognitoSignIn,
  signOut as cognitoSignOut,
} from './cognito';
import {
  DEV_AUTH,
  clearDevToken,
  devUserFromToken,
  loadDevToken,
  mintDevToken,
  needsFirstPassword,
  storeDevToken,
} from './dev-auth';
import { clearFederatedSession, getFederatedIdToken } from './federated';
import { roleFromGroups } from './roles';
import type { AuthContextValue, AuthStatus, AuthUser } from './types';

// O hook mora em ./context (módulo-folha, sem o SDK do Cognito); segue
// reexportado aqui para os consumidores do barril não mudarem.
export { useAuth } from './context';

/**
 * The active session's ID token, whichever login produced it. The federated
 * (Google Hosted UI) session is tried first — when both kinds exist, the login
 * that completed LAST is the one that should win, and the SRP sign-in path
 * actively clears the federated session for the same reason. Returns null when
 * neither exists (or both expired beyond refresh).
 */
async function currentIdToken(): Promise<string | null> {
  return (await getFederatedIdToken()) ?? (await getValidIdToken());
}

const MOCK_TOKEN = 'mock-access-token';

const MOCK_USER: AuthUser = {
  id: 'usr-0001',
  email: 'ana.souza@operadora.com.br',
  displayName: 'Ana Souza',
  role: 'admin',
};

/**
 * Auth context. In mock mode it auto-authenticates a seeded admin so the app
 * runs offline. In real mode it is backed by Amazon Cognito — password login
 * via SRP ({@link ./cognito}) and Google via the Hosted UI code+PKCE flow
 * ({@link ./federated}) — on the same public SPA client. Consumers see the same
 * surface either way; {@link AuthContextValue.getAccessToken} always yields the
 * Cognito **ID token** (the Bearer the API verifies), refreshed on demand.
 *
 * DEV_AUTH is checked BEFORE the mock shortcut: it is opt-in (VITE_DEV_AUTH=true
 * on a `vite dev` server only), and whoever turns it on is explicitly asking for
 * a real sign-in/sign-out cycle — including offline, where the E2E suite drives
 * the login flow and the protected-route redirect against the mock API.
 */
export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);

  useEffect(() => {
    if (DEV_AUTH) {
      const token = loadDevToken();
      const restored = token ? devUserFromToken(token) : null;
      setUser(restored);
      setStatus(restored ? 'authenticated' : 'unauthenticated');
      return;
    }

    if (USE_MOCKS) {
      setUser(MOCK_USER);
      setStatus('authenticated');
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        // Federated (Google) session first, then the SRP one — see currentIdToken.
        const token = await currentIdToken();
        const restored = token ? userFromIdToken(token) : null;
        if (cancelled) return;
        if (restored) {
          setUser(restored);
          setStatus('authenticated');
        } else {
          setUser(null);
          setStatus('unauthenticated');
        }
      } catch {
        if (cancelled) return;
        setUser(null);
        setStatus('unauthenticated');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (DEV_AUTH) return loadDevToken();
    if (USE_MOCKS) return MOCK_TOKEN;
    // Always the ID token; currentIdToken refreshes it when near expiry, for
    // both the federated and the SRP session.
    return currentIdToken();
  }, []);

  /** Mint + install a dev session (local backend, no Cognito). */
  const startDevSession = useCallback((email: string): void => {
    const token = mintDevToken(email.trim());
    const devUser = devUserFromToken(token);
    if (!devUser) throw new Error('Não foi possível criar a sessão de desenvolvimento.');
    storeDevToken(token);
    setUser(devUser);
    setStatus('authenticated');
  }, []);

  const signIn = useCallback(async (email: string, password: string): Promise<void> => {
    if (DEV_AUTH) {
      // Local backend: mint an unsigned token whose claims trust-gateway accepts.
      // The role is derived from the e-mail local-part (admin | editor | member),
      // and one local-part stands in for an account that was just invited.
      if (needsFirstPassword(email)) throw new NewPasswordRequiredError();
      startDevSession(email);
      return;
    }

    if (USE_MOCKS) {
      setUser({ ...MOCK_USER, email });
      setStatus('authenticated');
      return;
    }

    try {
      await cognitoSignIn(email, password);
    } catch (err) {
      // The forced first password change is not a failure, it is the next step
      // of the invitation — it travels typed so the login page can offer it
      // instead of translating it into "contate o administrador".
      if (err instanceof NewPasswordRequiredError) throw err;
      // Neither is an MFA challenge this console cannot answer: the password was
      // RIGHT, so the generic "verifique suas credenciais" would send the person
      // to retype a correct password forever. The typed error already carries
      // the sentence that tells them what actually has to happen.
      if (err instanceof MfaNotSupportedError) throw err;
      throw new Error(friendlyCognitoMessage(err));
    }

    // The password login just won: drop any federated (Google) session so the
    // newest sign-in is unambiguously the active one.
    clearFederatedSession();

    const token = await getValidIdToken();
    const signedIn = token ? userFromIdToken(token) : null;
    if (!signedIn) {
      throw new Error('Não foi possível concluir o login. Tente novamente.');
    }
    setUser(signedIn);
    setStatus('authenticated');
  }, [startDevSession]);

  const signOut = useCallback(async (): Promise<void> => {
    if (DEV_AUTH) clearDevToken();
    else if (!USE_MOCKS) {
      // Both session kinds, always: a Google session survives the SDK's own
      // signOut, and leaving it behind would resurrect the login on reload.
      clearFederatedSession();
      cognitoSignOut();
    }
    setUser(null);
    setStatus('unauthenticated');
  }, []);

  const changePassword = useCallback(
    async (oldPassword: string, newPassword: string): Promise<void> => {
      if (USE_MOCKS) {
        await new Promise((resolve) => setTimeout(resolve, 600));
        if (!oldPassword) {
          throw new Error('Senha atual incorreta.');
        }
        return;
      }
      await cognitoChangePassword(oldPassword, newPassword);
    },
    [],
  );

  const requestPasswordReset = useCallback(async (email: string): Promise<void> => {
    if (USE_MOCKS || DEV_AUTH) {
      // No real pool in offline/local modes — simulate a code being sent.
      await new Promise((resolve) => setTimeout(resolve, 400));
      return;
    }
    await cognitoRequestPasswordReset(email.trim());
  }, []);

  const confirmPasswordReset = useCallback(
    async (email: string, code: string, newPassword: string): Promise<void> => {
      if (USE_MOCKS || DEV_AUTH) {
        await new Promise((resolve) => setTimeout(resolve, 400));
        if (!code.trim()) {
          throw new Error('Código inválido. Confira o código enviado por e-mail.');
        }
        return;
      }
      await cognitoConfirmPasswordReset(email.trim(), code.trim(), newPassword);
    },
    [],
  );

  const completeFirstPassword = useCallback(
    async (email: string, temporaryPassword: string, newPassword: string): Promise<void> => {
      if (DEV_AUTH) {
        startDevSession(email);
        return;
      }
      if (USE_MOCKS) {
        setUser({ ...MOCK_USER, email });
        setStatus('authenticated');
        return;
      }

      try {
        await cognitoCompleteNewPassword(email.trim(), temporaryPassword, newPassword);
      } catch (err) {
        if (err instanceof MfaNotSupportedError) throw err;
        throw new Error(friendlyCognitoMessage(err));
      }

      // Answering the challenge already yields a session, so the person is in —
      // the same tail as signIn, which is what makes this the last step of the
      // invite rather than a detour back to the login form.
      const token = await getValidIdToken();
      const signedIn = token ? userFromIdToken(token) : null;
      if (!signedIn) {
        throw new Error('Não foi possível concluir o login. Tente novamente.');
      }
      setUser(signedIn);
      setStatus('authenticated');
    },
    [startDevSession],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      getAccessToken,
      signIn,
      signOut,
      changePassword,
      completeFirstPassword,
      requestPasswordReset,
      confirmPasswordReset,
    }),
    [
      status,
      user,
      getAccessToken,
      signIn,
      signOut,
      changePassword,
      completeFirstPassword,
      requestPasswordReset,
      confirmPasswordReset,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Decodes a Cognito ID token into the app's {@link AuthUser}. */
function userFromIdToken(jwt: string): AuthUser | null {
  const claims = decodeIdToken(jwt);
  if (!claims) return null;
  return {
    id: claims.sub,
    email: claims.email ?? '',
    // Resolved by decodeIdToken (declared name, then the e-mail's local part) and
    // never the subject: an account with neither simply has no name to show.
    displayName: claims.name ?? '',
    role: roleFromGroups(claims.groups),
  };
}

/** Maps Cognito SDK errors to friendly pt-BR copy for the login form. */
function friendlyCognitoMessage(err: unknown): string {
  const name =
    typeof err === 'object' && err !== null && 'name' in err
      ? String((err as { name: unknown }).name)
      : '';
  const code =
    typeof err === 'object' && err !== null && 'code' in err
      ? String((err as { code: unknown }).code)
      : '';
  const key = code || name;

  switch (key) {
    case 'NEW_PASSWORD_REQUIRED':
      return 'É necessário definir uma nova senha antes de entrar. Contate o administrador.';
    case 'NotAuthorizedException':
      return 'E-mail ou senha incorretos.';
    case 'UserNotFoundException':
      return 'E-mail ou senha incorretos.';
    case 'UserNotConfirmedException':
      return 'Sua conta ainda não foi confirmada. Contate o administrador.';
    case 'PasswordResetRequiredException':
      return 'É necessário redefinir sua senha antes de entrar.';
    case 'TooManyRequestsException':
    case 'LimitExceededException':
      return 'Muitas tentativas. Aguarde alguns instantes e tente novamente.';
    case 'NetworkError':
      return 'Não foi possível alcançar o servidor de autenticação. Verifique sua conexão.';
    default:
      return 'Não foi possível entrar. Verifique suas credenciais e tente novamente.';
  }
}
